import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';

export interface AddressState {
    defaultAddress: number | null;
    list: number[] | null;
    pendingDetail: AddressItem;
    detail: {
        [addressId: number]: AddressItem;
    };
    selectAddress: AddressItem | null;
}

class AddressItem implements Address.Item {
    public id: number;
    public name: string;
    public mobile: string;
    public tag: string;
    public district_id: number[];
    public district: string[];
    public detail: string;
    public is_default: boolean;
    public select_order_id?: string

    public constructor(item: Address.Item) {
        this.id = item.id;
        this.name = item.name;
        this.mobile = item.mobile;
        this.tag = item.tag;
        this.district_id = item.district_id;
        this.district = item.district;
        this.detail = item.detail;
        this.is_default = item.is_default;
    }

    public get address() {
        return this.district.join('') + this.detail;
    }
}

function parse(raw: Address.RawItem) {
    const item: Address.Item = {
        ...raw,
        district_id: [ raw.district1_id, raw.district2_id, raw.district3_id ].filter((_) => _) as number[],
        district: [ raw.district1, raw.district2, raw.district3 ].filter((_) => _) as string[],
    };
    return new AddressItem(item);
}

function format(item: Address.Item) {
    const raw: Address.RawItem = {
        ...item,
        district1: item.district[0],
        district2: item.district[1],
        district3: item.district[2],
        district1_id: item.district_id[0],
        district2_id: item.district_id[1],
        district3_id: item.district_id[2],
    };
    return raw;
}

const m: Module<AddressState, RootState> = {
    namespaced: true,
    state: {
        defaultAddress: null,
        list: null,
        pendingDetail: new AddressItem({
            id: 0,
            name: '',
            mobile: '',
            tag: '',
            district_id: [],
            district: [],
            detail: '',
            is_default: true,
        }),
        detail: {},
        selectAddress: null,
    },
    mutations: {
        setDefault(state, address) {
            state.defaultAddress = address.id;
            Vue.set(state.detail, address.id, address);
        },
        setList(state, list) {
            const ids: number[] = [];
            for (const item of list) {
                ids.push(item.id);
                Vue.set(state.detail, item.id, item);
            }
            state.list = ids;
        },
        setPendingDetail(state, detail) {
            state.pendingDetail = detail;
        },
        setDetail(state, { addressId, detail }) {
            Vue.set(state.detail, addressId, detail);
        },
        removeDetail(state, { addressId }) {
            const pos = (state.list || []).indexOf(addressId);
            if (pos >= 0) {
                state.list!.splice(pos, 1);
            }
        },
        setSelectAddress(state, info) {
            state.selectAddress = info;
        },
    },
    actions: {
        /**
         * 获取默认地址
         */
        async getDefault(context) {
            const response = await Vue.axios.get('/api/v1/user/default_address');
            const rdata = response.data;
            if (rdata.status === 0 && rdata.data) {
                context.commit('setDefault', parse(rdata.data));
            }
            return rdata;
        },
        /**
         * 获取默认地址
         */
        async getList(context, { forceUpdate } = {}) {
            if (context.state.list && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/user/address');
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setList', (rdata.data as Address.RawItem[]).map((item) => parse(item)));
            }
            return rdata;
        },
        /**
         * 获取默认地址
         */
        async getDetail(context, { addressId, forceUpdate }) {
            if (context.state.detail[addressId] && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/user/address_detail', {
                params: {
                    id: addressId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setDetail', {
                    addressId,
                    detail: parse(rdata.data),
                });
            }
            return rdata;
        },
        /**
         * 创建地址
         */
        async create(context) {
            const response = await Vue.axios.post('/api/v1/user/address', format(context.state.pendingDetail));
            const rdata = response.data;
            if (rdata.status === 0) {
                context.dispatch('getList', { forceUpdate: true });
            }
            return rdata;
        },
        /**
         * 更新地址
         */
        async update(context, { addressId }) {
            const detail = context.state.detail[addressId];
            const response = await Vue.axios.post('/api/v1/user/address', format(detail));
            const rdata = response.data;
            if (rdata.status === 0) {
                context.dispatch('getList', { forceUpdate: true });
            }
            return rdata;
        },
        /**
         * 删除地址
         */
        async delete(context, { addressId }) {
            const response = await Vue.axios.delete('/api/v1/user/address', {
                data: {
                    id: addressId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('removeDetail', { addressId });
            }
            return rdata;
        },
    },
};

export default m;
